import { useGlobalStore } from '@/stores/globalStore';

/**
 * postMessage bridge for partner WebViews that inject a native wallet bridge
 * object. The bridge name comes from merchant settings (`getWebviewBridgeName`);
 * `payload` and `messageType` are supplied by the caller at dispatch time.
 *
 * Message shape:
 * - { type: messageType, payload }
 *
 * Platform targets:
 * - iOS: `window.webkit.messageHandlers[bridgeName].postMessage(msg)`
 * - Android: `window[bridgeName].postMessage(JSON.stringify(msg))`
 *
 * If the configured bridge is not injected at dispatch time, fails silently
 * (defensive runtime no-op for WebView misconfiguration — eligibility is
 * resolved server-side).
 */
export default function useWebViewPostMessage() {
  const globalStore = useGlobalStore();

  const getBridgeName = () => {
    const settings = globalStore.getWebviewBridgeName ?? {};
    return settings;
  };

  const getIosHandler = (bridgeName) => (
    window.webkit?.messageHandlers?.[bridgeName]
  );

  const getAndroidBridge = (bridgeName) => window[bridgeName];

  /**
   * Whether the configured bridge object is actually injected in this WebView.
   */
  const isBridgeAvailable = () => {
    const bridgeName = getBridgeName();
    if (!bridgeName) return false;
    return Boolean(
      getIosHandler(bridgeName)?.postMessage
      || getAndroidBridge(bridgeName)?.postMessage,
    );
  };

  const postToBridge = (bridgeName, msg) => {
    const iosHandler = getIosHandler(bridgeName);
    if (iosHandler?.postMessage) {
      iosHandler.postMessage(msg);
      return true;
    }

    const androidBridge = getAndroidBridge(bridgeName);
    if (androidBridge?.postMessage) {
      androidBridge.postMessage(JSON.stringify(msg));
      return true;
    }

    return false;
  };

  /**
   * @param {string} orderId
   * @param {object} orderData
   */
  const dispatchWalletBridge = (payloadData, messageType) => {
    const bridgeName = getBridgeName();

    if (!payloadData || !isBridgeAvailable()) {
      return;
    }

    const msg = {
      type: messageType,
      payload: payloadData,
    };

    postToBridge(bridgeName, msg);
  };

  return {
    isBridgeAvailable,
    dispatchWalletBridge,
  };
}
